import { CircularProgress } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import SerpTable from '../../../../components/muiLegacy/Tables/SerpTable';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { FrontendUserEventGq } from '../../../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../../../graphql/users/usersApiService';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';

export interface SerpResultsDialogProps {
  project: ProjectGq;
  displaySerpResult: DisplaySerpResult;
  setDisplaySerpResult: (state: DisplaySerpResult) => void;
  onMouseDown?: (event: React.MouseEvent<any>) => void;
}

export interface DisplaySerpResult {
  keywordId: number;
  rankNumber: number;
}

export const SerpResultsDialog: React.FC<SerpResultsDialogProps> = ({
  project,
  displaySerpResult,
  setDisplaySerpResult,
  onMouseDown,
}) => {
  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_PROJECT_SERP,
      projectId: project.id,
    });
  }, [project.id]);

  const { keywords, query } = projectsApiService.queries.useGetKeywords({
    domainIds: project.domains?.map((d) => d.id),
    keywordQuery: { filtering: { projectId: project.id, keywordId: displaySerpResult.keywordId } },
  });

  const keyword = keywords?.find((k) => k.id === displaySerpResult.keywordId);
  const rankNumber = displaySerpResult.rankNumber;

  // Get data
  const serpQuery = projectsApiService.queries.useGetKeywordSerpResults(
    { keywordId: displaySerpResult.keywordId, projectId: project.id },
    { skip: !displaySerpResult.keywordId }
  );

  // Rank scroll logic
  const rankRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!!rankRef.current) {
      rankRef.current.style.position = 'relative';
      rankRef.current.scrollIntoView({ block: 'center' });
    }
  }, [rankRef.current]);

  // Close
  const handleClose = () => setDisplaySerpResult({ keywordId: undefined, rankNumber: undefined });

  // Render
  if (!keyword) {
    return null;
  }

  return (
    <>
      <VoxelDialog
        open={!!keyword}
        onClose={handleClose}
        title={'Search Engine Results Page'}
        subtitle={`Keyword: ${keyword.keyword}, Volume: ${formatNumber(
          keyword.volume
        )}, Date: ${moment
          .utc(keyword.serpResultTime)
          .format('LL')}, only covers organic and featured snippet results`}
        maxWClass="max-w-4xl"
        onMouseDown={(event: React.MouseEvent<any>) => {
          event.stopPropagation();
        }}
      >
        <VoxelDialogContent>
          <div className="overflow-y-auto overscroll-contain max-h-[480px]">
            {!serpQuery.result?.length && <CircularProgress />}
            <SerpTable
              project={project}
              serpResult={serpQuery.result}
              rankNumber={rankNumber}
              keyword={keyword}
            />
          </div>
        </VoxelDialogContent>
      </VoxelDialog>
    </>
  );
};

export default SerpResultsDialog;
